import {fromJS, List} from 'immutable'
import { get, post } from '../../util/http'
import * as schema from '../schema'
import { normalize } from 'normalizr'
import { denormalizeEntity, getEntity } from './entities'
import {getTopic, applyExplanationFilter} from './topic'
import {
  loadContentEntity,
  selectActiveContent,
  submitAnswer,
  revealAnswer,
  selectEntityIsLoading
} from './content'
import uniqueId from 'lodash/uniqueId'
import { wrapDispatch } from 'multireducer'
import { INPUT_ANSWER } from './content'

/**
 * Actions
 */
const ATTEMPT_GET_PROBLEM = 'expii/grandmaster/ATTEMPT_GET_PROBLEM'
export const PROCESS_PROBLEM = 'expii/grandmaster/PROCESS_PROBLEM'
const ADD_ACTION = 'expii/grandmaster/ADD_ACTION'
export const BATCH_POSTED = 'expii/grandmaster/BATCH_POSTED'
const CHOOSE_ACTIVE_SOLUTION_ID = 'expii/grandmaster/CHOOSE_ACTIVE_SOLUTION_ID'
const CHOOSE_ACTIVE_EXPLANATION_ID =
  'expii/grandmaster/CHOOSE_ACTIVE_EXPLANATION_ID'
const VIEW_EXPLANATIONS = 'expii/grandmaster/VIEW_EXPLANATIONS'
export const ADJUST_CURR_STREAK = 'expii/grandmaster/ADJUST_CURR_STREAK'
const CHANGE_VIEWING = 'expii/grandmaster/CHANGE_VIEWING'
const RESET_GRANDMASTER_SESSION = 'expii/grandmaster/RESET_GRANDMASTER_SESSION'

/**
 * Other Constants
 */

/**
 * Reducer
 */
const defaultGrandmasterState = fromJS({
  isFetching: false,
  gmProblem: {},
  batchHasPosted: false,
  answers: [],
  batch: {
    problem_id: null,
    base_topic_id: null,
    ts_problem_started: new Date().getTime() / 1000.0,
    actions: [] //Array of actions returned by createGMAction
  },
  activeSolutionId: -1,
  activeExplanationId: -1,
  isViewingExplanations: false,
  viewing: 'practice', //can be 'onboarding', 'practice', or 'scores'
  problemsInSession: []
})

export default function reducer(state = defaultGrandmasterState, action = {}) {
  //const answers = Set()
  switch (action.type) {
  case ATTEMPT_GET_PROBLEM:
    return state.merge(
      fromJS({
        isFetching: true,
        batchHasPosted: false,
        answers: [],
        isViewingExplanations: false,
        viewing: 'practice'
      })
    )
  case PROCESS_PROBLEM:
    return state.merge(
      fromJS({
        gmProblem: action.payload.gmProblem,
        activeSolutionId: action.payload.activeSolutionId,
        activeExplanationId: action.payload.activeExplanationId,
        viewing: action.payload.viewing,
        batch: {
          problem_id: action.payload.problemId,
          base_topic_id: action.payload.baseTopicId,
          ts_problem_started: new Date().getTime() / 1000.0,
          actions: []
        },
        isFetching: false,
      })
    )
  case ADD_ACTION:
    return state.updateIn(['batch', 'actions'], list =>
      list.push(fromJS(action.gmAction))
    )
  case BATCH_POSTED:
    return state.withMutations(s => {
      s = s.set('batchHasPosted', true)
      s = s.update('problemsInSession', p => p.push(s.get('gmProblem')))
      return s
    })
  case CHOOSE_ACTIVE_SOLUTION_ID:
    return state.set('activeSolutionId', action.solutionId)
  case CHOOSE_ACTIVE_EXPLANATION_ID:
    return state.set('activeExplanationId', action.explanationId)
  case INPUT_ANSWER: {
    if (action.meta.__multireducerKey === 'grandmasterProblem') {
      const {answerType} = action
      if (answerType === 'text_entry' || answerType === 'multiple_choice') {
        return state.set('answers', List([action.answer]))
      } else {
        const answerIndex =  state.get('answers').indexOf(action.answer)
        if (answerIndex !== -1) {
          return state.deleteIn(['answers', answerIndex])
        } else {
          return state.update('answers', list => list.push(action.answer))
        }
      }
    } else {
      return state
    }
  }
  case VIEW_EXPLANATIONS:
    return state.set('isViewingExplanations', action.shouldBeViewing)
  case CHANGE_VIEWING:
    return state.set('viewing', action.viewing)
  case RESET_GRANDMASTER_SESSION:
    return defaultGrandmasterState
  default:
    return state
  }
}

/**
 * Action Creators
 */

/**
 * returns GrandmasterAction object compatible with GrandmasterBatch
 * @param  {string} actionType     can be: 'correct_answer', 'skip_problem', 'reveal_solutions', 'exit_stream', 'reveal_related_topics', 'report_problem', 'incorrect_answer'
 * @param  {[type]} additionalData [description]
 * @return {Object}                 action object
 */
const createGMAction = (actionType, additionalData) => ({
  action_type: actionType,
  timestamp: new Date().getTime() / 1000.0,
  additional_data: additionalData
})

export function attemptGetProblem() {
  return { type: ATTEMPT_GET_PROBLEM }
}

export function processProblem(problem_data) {
  return { type: PROCESS_PROBLEM, payload: problem_data }
}

export function addAction(actionType, additionalData) {
  return {
    type: ADD_ACTION,
    gmAction: createGMAction(actionType, additionalData)
  }
}

export function chooseActiveSolutionId(solutionId) {
  return { type: CHOOSE_ACTIVE_SOLUTION_ID, solutionId }
}

export function chooseActiveExplanationIdGm(explanationId) {
  return { type: CHOOSE_ACTIVE_EXPLANATION_ID, explanationId }
}

export function batchPosted(new_scores) {
  return { type: BATCH_POSTED, new_scores }
}

export function viewExplanations(shouldBeViewing) {
  return { type: VIEW_EXPLANATIONS, shouldBeViewing }
}

function incrementCurrStreak(gmProblem) {
  return {type: ADJUST_CURR_STREAK, increment: true, gmProblem}
}

function resetCurrStreak(gmProblem) {
  return {type: ADJUST_CURR_STREAK, increment: false, gmProblem}
}

export function changeViewing(viewing) {
  return {type: CHANGE_VIEWING, viewing}
}

export function resetGrandmasterSession() {
  return {type: RESET_GRANDMASTER_SESSION}
}

/**
 * Side Effects
 */
export function getGMProblem(topicId, problemId = 0) {
  return (dispatch, getState) => {
    dispatch(attemptGetProblem())
    dispatch(applyExplanationFilter('All'))

    return get(
      `/api/v2/grandmaster/${topicId}/next_problem/landing/${problemId}`
    ).then(json => {
      let { success, output } = json
      output['id'] = uniqueId('gmProblem')
      const normalized = normalize(output, schema.gmProblem)
      const normalizedEntity = normalized.entities.gmProblem[normalized.result]
      const activeSolutionId = output.problem.solutions.length
        ? output.problem.solutions[0].id
        : -1
      const activeExplanationId = output.topic.explanations.length
        ? output.topic.explanations[0].id
        : -1
      json = {
        success: success,
        gmProblem: normalized.result,
        baseTopicId: normalizedEntity.baseTopic,
        problemId: normalizedEntity.problem,
        activeSolutionId: activeSolutionId,
        activeExplanationId: activeExplanationId,
        viewing: output.showOnboarding ? 'onboarding' : 'practice',
        entities: normalized.entities
      }
      dispatch(processProblem(json))
      dispatch(getTopic(normalizedEntity.baseTopic))
      wrapDispatch(dispatch, 'grandmasterProblem')(
        loadContentEntity('problem', normalizedEntity.problem)
      )
      if (activeSolutionId !== -1) {
        wrapDispatch(dispatch, 'grandmasterSolution')(
          loadContentEntity('solution', activeSolutionId)
        )
      }
      if (activeExplanationId !== -1) {
        wrapDispatch(dispatch, 'grandmasterExplanation')(
          loadContentEntity('explanation', activeExplanationId)
        )
      }
    })
  }
}

export function getGMProblemProblemId(problemId) {
  return (dispatch, getState) => {
    //We want to get the problem's main topic id first
    return get(`/api/content/problem/${problemId}`)
      .then(json => {
        const {output} = json
        dispatch(getGMProblem(output.topicId, problemId))
      })
  }
}

export function submitAnswerGrandmaster(blockIndex) {
  return (dispatch, getState) => {
    wrapDispatch(dispatch, 'grandmasterProblem')(submitAnswer(blockIndex))
    const state = getState()
    const actionsLength = state.getIn(['grandmaster','batch','actions']).size
    const gmProblem = state.getIn(['grandmaster','gmProblem'])
    const activeContent = selectActiveContent(state, 'grandmasterProblem')
    const isCorrect = activeContent.getIn([
      'renderedContent',
      blockIndex,
      'content',
      'isCorrect'
    ])
    const actionType = isCorrect ? 'correct_answer' : 'incorrect_answer'
    if (!actionsLength && isCorrect) {
      dispatch(incrementCurrStreak(gmProblem))
    } else {
      dispatch(resetCurrStreak(gmProblem))
    }
    dispatch(addAction(actionType, null))
    if (isCorrect) {
      dispatch(postBatch())
    }
  }
}

export function revealSolutionGrandmaster(blockIndex) {
  return (dispatch, getState) => {
    wrapDispatch(dispatch, 'grandmasterProblem')(revealAnswer(blockIndex))
    dispatch(addAction('reveal_solutions', null))
    dispatch(postBatch())
  }
}

export function exitGrandmaster() {
  return (dispatch, getState) => {
    // const batchHasPosted = getState().getIn(['grandmaster','batch','batchHasPosted'])
    // if(!batchHasPosted) {
    //   dispatch(addAction('exit_stream',null))
    //   dispatch(postBatch())
    // }
  }
}

export function postBatch() {
  return (dispatch, getState) => {
    const batch = getState()
      .getIn(['grandmaster', 'batch'])
      .toJS()
    return post('/api/v2/grandmaster/insert_batch', { data: batch }).then(
      json => {
        dispatch(batchPosted(json.output))
      }
    )
  }
}

export function chooseActiveSolution(solutionId) {
  return (dispatch, getState) => {
    wrapDispatch(dispatch, 'grandmasterSolution')(
      loadContentEntity('solution', solutionId)
    )
    dispatch(chooseActiveSolutionId(solutionId))
  }
}

export function chooseActiveExplanationGm(explanationId) {
  return (dispatch, getState) => {
    wrapDispatch(dispatch, 'grandmasterExplanation')(
      loadContentEntity('explanation', explanationId)
    )
    dispatch(chooseActiveExplanationIdGm(explanationId))
  }
}

/**
 * Selectors
 */
export const selectTopicScores = state => {
  const domain = state.get('grandmaster')
  const baseTopicId = state.getIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'baseTopic'
  ])
  const scores = state.getIn(['entities', 'topic', `${baseTopicId}`, 'scores'])
  return {
    scores: scores
  }
}

export const selectProblem = state => {
  const domain = state.get('grandmaster')
  const problem = selectActiveContent(state, 'grandmasterProblem')
  const reason = state.getIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'reason'
  ])
  const streak = state.hasIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'streak'])
    ? state.getIn([
      'entities',
      'gmProblem',
      domain.get('gmProblem'),
      'streak'
    ])
    : 0
  const bestStreak = state.hasIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'bestStreak'])
    ? state.getIn([
      'entities',
      'gmProblem',
      domain.get('gmProblem'),
      'bestStreak'
    ])
    : 0
  const baseTopicId = state.getIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'baseTopic'
  ])
  return {
    isFetching: domain.get('isFetching'),
    problem: problem,
    reason: reason,
    streak: streak,
    bestStreak: bestStreak,
    baseTopicId: baseTopicId,
    didGetProblem: !problem.isEmpty(),
    hasInputAnswer: !domain.get('answers').isEmpty(),
    batchHasPosted: domain.get('batchHasPosted')
  }
}

export const selectActiveSolution = state => {
  const domain = state.get('grandmaster')
  const activeSolutionId = domain.get('activeSolutionId')
  const isLoading = selectEntityIsLoading(state, 'grandmasterSolution')
  if (activeSolutionId !== -1) {
    const problemId = state.getIn([
      'entities',
      'gmProblem',
      domain.get('gmProblem'),
      'problem'
    ])
    //const solutions = problem.get("solutions"); When solutions are upvoted, this will not properly reflect that
    const problem  = state.getIn(['entities', 'problem', problemId.toString()])
    const allSolutions = state.getIn(['entities','problem_solution'])
    let solutions = List()
    if (problem) {
      const problemSolutions = problem.get('solutions')
      solutions = allSolutions.filter((v,k)=> {
        return problemSolutions.includes(parseInt(k))
      })
    }
    return {
      isLoading,
      activeSolution: selectActiveContent(state, 'grandmasterSolution'),
      solutions: solutions.toIndexedSeq().toList(),
      batchHasPosted: domain.get('batchHasPosted')
    }
  } else {
    return {
      isLoading,
      activeSolution: {},
      solutions: [],
      batchHasPosted: domain.get('batchHasPosted')
    }
  }
}

export const selectCurrentGmTopic = state => {
  const domain = state.get('grandmaster')
  const topicId = state.getIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'topic'
  ])
  const topic = denormalizeEntity(state, topicId, schema.topic)
  return {topic}
}

export const selectActiveExplanation = state => {
  const domain = state.get('grandmaster')
  const activeExplanationId = domain.get('activeExplanationId')
  const isLoading = selectEntityIsLoading(state, 'grandmasterExplanation')
  if (activeExplanationId !== -1) {
    const topicId = state.getIn([
      'entities',
      'gmProblem',
      domain.get('gmProblem'),
      'topic'
    ])
    const topic = denormalizeEntity(state, topicId, schema.topic)
    const explanationFilter = state.getIn(['topic', 'explanationFilter'])
    let explanations = []
    if (explanationFilter) {
      explanations = topic
        .get('explanations')
        .filter(e => e.get('contentType') === explanationFilter)
    } else {
      explanations = topic.get('explanations')
    }
    return {
      isLoading,
      activeExplanation: selectActiveContent(state, 'grandmasterExplanation'),
      explanations: explanations
    }
  } else {
    return {
      isLoading,
      activeExplanation: {},
      explanations: []
    }
  }
}


export const selectScoreScreen = state => {
  const domain = state.get('grandmaster')
  const numProblems = domain.get('problemsInSession').size
  const numCorrectProblems = domain.get('problemsInSession')
    .map((gmProblem) => {
      const problem = getEntity(state, 'gmProblem', gmProblem)
      return problem ? problem.get('gotCorrect') : false
    })
    .filter((result) => result === true).size
  const streak = state.hasIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'streak'])
    ? state.getIn([
      'entities',
      'gmProblem',
      domain.get('gmProblem'),
      'streak'
    ])
    : 0
  const bestStreak = state.hasIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'bestStreak'])
    ? state.getIn([
      'entities',
      'gmProblem',
      domain.get('gmProblem'),
      'bestStreak'
    ])
    : 0

  const topicId = state.getIn([
    'entities',
    'gmProblem',
    domain.get('gmProblem'),
    'baseTopic'
  ])
  const topic = denormalizeEntity(state, topicId, schema.topic)
  const slug = topic.get('slug')
  return {
    numProblems,
    numCorrectProblems,
    streak,
    bestStreak,
    slug
  }
}



// WEBPACK FOOTER //
// ./src/redux/modules/grandmaster.js